// ThreadsView — activity list for participated threads.

import { useState, type CSSProperties, type ReactNode } from "react";
import { GitBranch } from "lucide-react";
import { RelativeLabel } from "@nurion/ui/relative-label";

import { EmptyState } from "~/components/EmptyState";
import { PersonAvatar } from "~/components/PersonAvatar";
import { useMatrix } from "~/context/MatrixContext";
import { useToast } from "~/context/ToastContext";
import { useThreadsFeed } from "~/hooks/useThreadsFeed";
import type { ThreadsFeedItem } from "~/lib/matrix";
import { absoluteFallback, LOCAL_ZONE } from "~/lib/relative-time";
import { ICON_STROKE } from "~/lib/icon";

const ROW_STYLE_ID = "chat-threads-row-styles";

function injectRowStyles(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById(ROW_STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = ROW_STYLE_ID;
  style.textContent = `
    .chat-threads-row:hover { background: var(--chat-surface-raised); }
    @keyframes chat-threads-shimmer-pulse {
      0%, 100% { background-color: var(--chat-surface-raised); }
      50% { background-color: var(--chat-surface-overlay); }
    }
    .chat-threads-shimmer {
      animation: chat-threads-shimmer-pulse var(--chat-motion-slow) var(--chat-motion-ease) infinite;
    }
    @media (prefers-reduced-motion: reduce) {
      .chat-threads-shimmer { animation: none; }
    }
  `;
  document.head.appendChild(style);
}
injectRowStyles();

export function ThreadsView() {
  const { items, totalUnread, isLoading, markAllRead, markThreadRead } = useThreadsFeed();
  const { setActiveRoomId, openThread } = useMatrix();
  const { push: pushToast } = useToast();
  const [marking, setMarking] = useState(false);

  const handleMarkAll = async () => {
    setMarking(true);
    await markAllRead();
    setMarking(false);
    pushToast({ message: "Marked threads read." });
  };

  if (isLoading && items.length === 0) {
    return (
      <ViewShell title="Threads">
        <ThreadsShimmer />
      </ViewShell>
    );
  }

  const action =
    totalUnread > 0
      ? { label: marking ? "Marking…" : "Mark all read", onClick: handleMarkAll, disabled: marking }
      : null;

  return (
    <ViewShell title="Threads" action={action}>
      {items.length === 0 ? (
        <EmptyState icon={<ThreadsIllustration />} primary="You're not in any threads yet." />
      ) : (
        <ul
          style={{
            flex: 1,
            overflowY: "auto",
            listStyle: "none",
            margin: 0,
            padding: 0,
          }}
        >
          {items.map((item) => (
            <li key={`${item.roomId}:${item.rootEventId}`}>
              <ThreadRow
                item={item}
                onOpen={() => {
                  void markThreadRead(item);
                  setActiveRoomId(item.roomId);
                  openThread({ roomId: item.roomId, root: item.rootEvent });
                }}
              />
            </li>
          ))}
        </ul>
      )}
    </ViewShell>
  );
}

function ViewShell({
  title,
  count,
  action,
  children,
}: {
  title: string;
  count?: number;
  action?: { label: string; onClick: () => void; disabled?: boolean } | null;
  children: ReactNode;
}) {
  return (
    <section
      data-testid="threads-view"
      style={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minWidth: 0,
        height: "100%",
        background: "var(--chat-surface-app)",
        color: "var(--chat-fg-primary)",
        fontFamily: "var(--chat-font-sans)",
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--chat-space-3)",
          height: "var(--chat-timeline-room-header-height)",
          padding: "0 var(--chat-space-4)",
          borderBottom: "1px solid var(--chat-surface-border)",
          flexShrink: 0,
        }}
      >
        <GitBranch
          size={16}
          strokeWidth={ICON_STROKE}
          style={{ color: "var(--chat-fg-secondary)", flexShrink: 0 }}
        />
        <h2
          style={{
            flex: 1,
            margin: 0,
            color: "var(--chat-fg-primary)",
            fontFamily: "var(--chat-font-display)",
            fontSize: "var(--chat-type-display-sm-size)",
            lineHeight: "var(--chat-type-display-sm-line)",
            fontWeight: 300,
          }}
        >
          {title}
          {count !== undefined && (
            <span
              style={{
                marginLeft: "var(--chat-space-2)",
                color: "var(--chat-fg-secondary)",
                fontFamily: "var(--chat-font-sans)",
                fontSize: "var(--chat-type-body-md-size)",
                lineHeight: "var(--chat-type-body-md-line)",
                fontWeight: 400,
              }}
            >
              · {count}
            </span>
          )}
        </h2>
        {action && (
          <button
            type="button"
            onClick={action.onClick}
            disabled={action.disabled === true}
            className="chat-button"
            style={ghostButtonStyle}
          >
            {action.label}
          </button>
        )}
      </header>
      {children}
    </section>
  );
}

function ThreadRow({ item, onOpen }: { item: ThreadsFeedItem; onOpen: () => void }) {
  const { client } = useMatrix();
  return (
    <button
      type="button"
      onClick={onOpen}
      className="chat-threads-row"
      aria-label={`Open thread by ${item.rootSender} in ${item.roomName}`}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--chat-space-3)",
        width: "100%",
        height: 80,
        padding: "0 var(--chat-space-4)",
        border: "none",
        borderBottom: "1px solid var(--chat-surface-border)",
        background: "transparent",
        color: "inherit",
        font: "inherit",
        textAlign: "left",
        cursor: "pointer",
      }}
    >
      <PersonAvatar
        client={client}
        avatarUrl={item.rootEvent.sender.avatarUrl}
        name={item.rootSender}
        size={28}
        radius="var(--chat-radius-md)"
      />
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: "var(--chat-space-2)" }}>
          <LineStrong>{item.rootSender}</LineStrong>
          <RoomChip roomName={item.roomName} />
          <MutedText>
            <RelativeLabel
              value={new Date(item.lastReplyTs).toISOString()}
              zone={LOCAL_ZONE}
              fallback={absoluteFallback(item.lastReplyTs)}
            />
          </MutedText>
        </div>
        <LineMuted>{item.rootBodyPreview}</LineMuted>
        <div style={{ display: "flex", alignItems: "center", gap: "var(--chat-space-2)" }}>
          <LineMuted>
            {item.lastReplySender}: {item.lastReplyBodyPreview}
          </LineMuted>
          {item.unreadReplyCount > 0 && <UnreadDot />}
        </div>
      </div>
    </button>
  );
}

function LineStrong({ children }: { children: ReactNode }) {
  return <span style={strongLineStyle}>{children}</span>;
}

function LineMuted({ children }: { children: ReactNode }) {
  return <span style={mutedLineStyle}>{children}</span>;
}

function MutedText({ children }: { children: ReactNode }) {
  return <span style={metaTextStyle}>{children}</span>;
}

function RoomChip({ roomName }: { roomName: string }) {
  return <span style={roomChipStyle}>{roomName}</span>;
}

function UnreadDot() {
  return <span className="chat-threads-unread-dot" aria-hidden style={dotStyle} />;
}

function ThreadsIllustration() {
  return (
    <svg aria-hidden width={80} height={80} viewBox="0 0 80 80" fill="none">
      <circle cx={24} cy={24} r={7} stroke="var(--chat-fg-muted)" strokeWidth={ICON_STROKE} />
      <circle cx={56} cy={24} r={7} stroke="var(--chat-fg-muted)" strokeWidth={ICON_STROKE} />
      <circle cx={40} cy={56} r={7} stroke="var(--chat-fg-muted)" strokeWidth={ICON_STROKE} />
      <path
        d="M30 27 C38 34 42 34 50 27 M38 51 C32 42 28 36 25 31 M42 51 C48 42 52 36 55 31"
        stroke="var(--chat-surface-raised)"
        strokeWidth={ICON_STROKE}
        strokeLinecap="round"
      />
    </svg>
  );
}

function ThreadsShimmer() {
  return (
    <div aria-hidden style={{ flex: 1, padding: "var(--chat-space-2)", display: "grid", gap: 4 }}>
      {[0, 1, 2, 3, 4].map((index) => (
        <div
          key={index}
          className="chat-threads-shimmer"
          style={{ height: 80, borderRadius: "var(--chat-radius-sm)" }}
        />
      ))}
    </div>
  );
}

const strongLineStyle: CSSProperties = {
  color: "var(--chat-fg-primary)",
  fontFamily: "var(--chat-font-sans)",
  fontSize: "var(--chat-type-body-md-size)",
  lineHeight: "var(--chat-type-body-md-line)",
  fontWeight: 400,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const mutedLineStyle: CSSProperties = {
  flex: 1,
  color: "var(--chat-fg-secondary)",
  fontFamily: "var(--chat-font-sans)",
  fontSize: "var(--chat-type-body-sm-size)",
  lineHeight: "var(--chat-type-body-sm-line)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  minWidth: 0,
};

const metaTextStyle: CSSProperties = {
  color: "var(--chat-fg-muted)",
  fontFamily: "var(--chat-font-sans)",
  fontSize: "var(--chat-type-body-xs-size)",
  lineHeight: "var(--chat-type-body-xs-line)",
  flexShrink: 0,
};

const roomChipStyle: CSSProperties = {
  maxWidth: 120,
  padding: "0 6px",
  height: 18,
  borderRadius: "var(--chat-radius-sm)",
  background: "var(--chat-surface-raised)",
  color: "var(--chat-fg-secondary)",
  fontFamily: "var(--chat-font-sans)",
  fontSize: "var(--chat-type-body-xs-size)",
  lineHeight: "18px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  flexShrink: 0,
};

const dotStyle: CSSProperties = {
  width: 6,
  height: 6,
  borderRadius: "var(--chat-radius-full)",
  background: "var(--chat-accent)",
  flexShrink: 0,
};

const ghostButtonStyle: CSSProperties = {
  padding: "var(--chat-space-1) var(--chat-space-3)",
  borderRadius: "var(--chat-radius-sm)",
  border: "none",
  background: "transparent",
  color: "var(--chat-fg-secondary)",
  fontFamily: "var(--chat-font-sans)",
  fontSize: "var(--chat-type-body-sm-size)",
  lineHeight: "var(--chat-type-body-sm-line)",
  fontWeight: 500,
  cursor: "pointer",
};
